import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { registerRequest } from "../../api/auth";

const RequestAccessPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    farmName: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await registerRequest({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        farmName: formData.farmName.trim(),
        message: formData.message.trim() || undefined,
      });
      setSuccessMessage(response.message || "Your access request has been submitted.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        farmName: "",
        message: "",
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", p: 2 }}
    >
      <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 520 }}>
        <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
          Request Access
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Submit your details. A super admin will review your request and add you to the right tenant.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField fullWidth label="Full Name" value={formData.name} onChange={handleChange("name")} required margin="normal" />
          <TextField fullWidth label="Email" type="email" value={formData.email} onChange={handleChange("email")} required margin="normal" />
          <TextField fullWidth label="Phone Number" value={formData.phone} onChange={handleChange("phone")} required margin="normal" />
          <TextField fullWidth label="Farm / Organization Name" value={formData.farmName} onChange={handleChange("farmName")} required margin="normal" />
          <TextField
            fullWidth
            label="Additional Notes (optional)"
            value={formData.message}
            onChange={handleChange("message")}
            margin="normal"
            multiline
            minRows={3}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 2, mb: 2 }}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit Request"}
          </Button>
        </form>

        <Box textAlign="center">
          <RouterLink to="/login" style={{ color: "#1976d2", textDecoration: "none" }}>
            Back to Sign In
          </RouterLink>
        </Box>
      </Paper>
    </Box>
  );
};

export default RequestAccessPage;
